import { memo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  Flex,
  Text,
  Heading,
  Icon,
  Button,
  Tag,
  Stack,
  Skeleton,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { LuFolder, LuFileText, LuPlus, LuEllipsisVertical, LuPencil, LuArchive, LuTrash2 } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'

interface ProjectsListViewProps {
  projects: any[]
  isLoading: boolean
  onProjectClick: (project: any) => void
  onCreateProject: () => void
  onOpenWorkspace?: (project: any) => void
  onEditProject?: (project: any) => void
  onArchiveProject?: (project: any) => void
  onDeleteProject?: (project: any) => void
  cardBg: string
  borderColor: string
}

export const ProjectsListView = memo(function ProjectsListView({
  projects,
  isLoading,
  onProjectClick,
  onCreateProject,
  onOpenWorkspace,
  onEditProject,
  onArchiveProject,
  onDeleteProject,
  cardBg,
}: ProjectsListViewProps) {
  const { t } = useTranslation()
  const getSpecialtyLabel = (specialty: string) => {
    const labels: Record<string, string> = {
      parasitology: 'Parasitologie',
      hematology: 'Hématologie',
      radiology: 'Radiologie',
      immunology: 'Immunologie',
      general: 'Général',
    }
    return labels[specialty] || specialty
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      research: 'Recherche',
      writing: 'Rédaction',
      clinical_review: 'Revue clinique',
      case_study: 'Étude de cas',
    }
    return labels[type] || type
  }

  if (isLoading) {
    return (
      <Stack spacing={4}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="150px" borderRadius="lg" />
        ))}
      </Stack>
    )
  }

  if (projects.length === 0) {
    return (
      <Card bg={cardBg} py={12}>
        <CardBody>
          <VStack spacing={4}>
            <Icon as={LuFolder} boxSize={16} color="gray.300" />
            <Text fontSize="lg" color="gray.500">
              {t('medinsight.noProjects')}
            </Text>
            <Button leftIcon={<LuPlus />} colorScheme="blue" onClick={onCreateProject}>
              {t('medinsight.createFirstProject')}
            </Button>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {t('medinsight.myProjects')}
        </Text>
        <Button
          leftIcon={<LuPlus />}
          colorScheme="purple"
          size="md"
          onClick={onCreateProject}
          borderRadius="lg"
          boxShadow="sm"
          _hover={{
            transform: 'translateY(-2px)',
            boxShadow: 'md',
          }}
          transition="all 0.2s"
        >
          {t('medinsight.newProject')}
        </Button>
      </Flex>

      <VStack spacing={5} align="stretch">
        {projects.map((project) => (
          <Card
            key={project.id}
            bg={cardBg}
            borderRadius="2xl"
            borderLeftWidth="4px"
            borderLeftColor={`${project.color}.400`}
            boxShadow="md"
            cursor="pointer"
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            overflow="hidden"
            position="relative"
            _hover={{
              boxShadow: 'xl',
              transform: 'translateY(-4px) scale(1.01)',
              borderLeftColor: `${project.color}.600`,
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgGradient: `linear(to-r, ${project.color}.50, transparent)`,
              opacity: 0.3,
              pointerEvents: 'none',
            }}
            onClick={(e) => {
              // Only navigate if not clicking on the menu
              if (!(e.target as HTMLElement).closest('[data-project-menu]')) {
                // Ouvrir directement l'espace de travail au lieu de la vue détaillée
                if (onOpenWorkspace) {
                  onOpenWorkspace(project)
                } else {
                  onProjectClick(project)
                }
              }
            }}
          >
            <CardBody p={6} position="relative" zIndex={1}>
              <HStack justify="space-between" align="start" spacing={4}>
                <HStack align="start" spacing={4} flex={1}>
                  {/* Icon with colored background */}
                  <Box
                    p={3}
                    bgGradient={`linear(135deg, ${project.color}.400, ${project.color}.600)`}
                    borderRadius="xl"
                    boxShadow="md"
                  >
                    <Icon as={LuFolder} boxSize={6} color="white" />
                  </Box>

                  <VStack align="start" spacing={3} flex={1}>
                    <VStack align="start" spacing={1}>
                      <Heading size="md" fontWeight="semibold">
                        {project.title}
                      </Heading>
                      {project.description && (
                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                          {project.description}
                        </Text>
                      )}
                    </VStack>

                    <HStack spacing={2} flexWrap="wrap">
                      <Tag
                        size="sm"
                        colorScheme="blue"
                        borderRadius="full"
                        fontWeight="medium"
                      >
                        {getSpecialtyLabel(project.specialty)}
                      </Tag>
                      <Tag
                        size="sm"
                        colorScheme="purple"
                        borderRadius="full"
                        fontWeight="medium"
                      >
                        {getTypeLabel(project.type)}
                      </Tag>
                      <Tag
                        size="sm"
                        colorScheme={project.status === 'active' ? 'green' : 'gray'}
                        borderRadius="full"
                        fontWeight="medium"
                      >
                        {project.status === 'active' ? 'Actif' : 'Archivé'}
                      </Tag>
                    </HStack>
                  </VStack>
                </HStack>

                <VStack spacing={3} align="end" minW="120px">
                  <HStack
                    fontSize="sm"
                    color="gray.600"
                    bg="gray.50"
                    px={3}
                    py={1.5}
                    borderRadius="lg"
                  >
                    <Icon as={LuFileText} boxSize={4} />
                    <Text fontWeight="medium">{project.sourceCount || 0} sources</Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                    {new Date(project.updatedAt).toLocaleDateString('fr-FR')}
                  </Text>

                  {/* Actions Menu */}
                  {(onEditProject || onArchiveProject || onDeleteProject) && (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<LuEllipsisVertical />}
                        variant="ghost"
                        size="sm"
                        data-project-menu
                        onClick={(e) => e.stopPropagation()}
                        borderRadius="lg"
                        _hover={{
                          bg: 'gray.100',
                        }}
                      />
                      <MenuList data-project-menu borderRadius="xl" boxShadow="xl">
                        {onEditProject && (
                          <MenuItem
                            icon={<LuPencil />}
                            onClick={(e) => {
                              e.stopPropagation()
                              onEditProject(project)
                            }}
                            borderRadius="md"
                          >
                            Renommer
                          </MenuItem>
                        )}
                        {onArchiveProject && (
                          <MenuItem
                            icon={<LuArchive />}
                            onClick={(e) => {
                              e.stopPropagation()
                              onArchiveProject(project)
                            }}
                            borderRadius="md"
                          >
                            {project.status === 'active' ? 'Archiver' : 'Désarchiver'}
                          </MenuItem>
                        )}
                        {onDeleteProject && (
                          <MenuItem
                            icon={<LuTrash2 />}
                            color="red.600"
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteProject(project)
                            }}
                            borderRadius="md"
                          >
                            Supprimer
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  )}
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </VStack>
    </VStack>
  )
})
